import {
  Contact_Create,
  Contact_Update_Destroyable,
  Destroyable,
  DispatchNumber_Update,
  OrderUnitOfMeasure,
  Phase_Create,
  Phase_Read,
  Phase_Read_Typeahead,
  Phase_Update,
  Project_Create,
  Project_Read,
  Project_Read_Nested,
  Project_Read_Typeahead,
  Project_Read_Typeahead_External_Id,
  Project_Update,
  ProjectEmailDraft_Read,
  ProjectMaterialTotal_Create,
  ProjectMaterialTotal_Read,
  ProjectMaterialTotal_Update,
  ProjectState,
  ProjectStateEvent,
  ProjectSummary_Read_Nested,
  RateType,
  User_Read,
  Waypoint_Create,
  Waypoint_Update_Destroyable,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { cloneDeep, isNil } from 'lodash-es';

import { ContactTypes, contactTypesOptions } from '~constants/enums';
import { AccountBasic } from '~hooks/useAccount/models';
import { CompanyBasic } from '~hooks/useCompany/models';
import { ContactItem } from '~hooks/useContact/models/ContactItem';
import { BasicDepartment } from '~hooks/useDepartment/models';
import { EquipmentTypeBasic } from '~hooks/useEquipment/models';
import { BasicMaterial } from '~hooks/useMaterials/models';
import {
  OutboundEmailDraft,
  OutboundEmailDraftFields,
} from '~hooks/useOutboundEmails/models';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { BasicServiceClass } from '~hooks/useServiceClass/models';
import { Service } from '~hooks/useServices/models';
import { SiteBasic, WayPoint } from '~hooks/useSites/models';
import { TaxRateScheduleBasic } from '~hooks/useTaxRateSchedule/models';
import {
  deparseRateDetailsDTO,
  deparseUpdateRateDetailsDTO,
} from '~pages/Sales/Orders/NewOrderForm/schema';
import { ProjectFormSchemaInterface } from '~pages/Sales/Projects/projectFormSchema';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import { parseFiniteNumber } from '~utils/utilFunctions';

import { User, UserBasic } from '../useUsers/models';

export class ExtendedContactItem extends ContactItem {
  public static parse(proto: any): ExtendedContactItem {
    return new ExtendedContactItem(
      proto.id ?? '',
      proto.name ?? '',
      proto.email ?? '',
      proto.phone ?? '',
      proto.type ? contactTypesOptions.filter((item) => item.id === proto.type)[0] : null,
      proto._destroy ?? undefined,
    );
  }

  public static deparseUpdate(
    proto: Contact_Create & Destroyable,
  ): Contact_Update_Destroyable {
    return {
      id: proto.id,
      name: proto.name,
      email: proto.email,
      phone: proto.phone,
      _destroy: proto._destroy || undefined,
    };
  }

  public get type(): Nullable<ItemNameAndId> {
    return this._type;
  }
  public set type(value: ItemNameAndId) {
    this._type = value;
  }
  constructor(
    _id: string,
    _name: string,
    _email: string,
    _phone: string,
    private _type: Nullable<ItemNameAndId>,
    private _destroy: number | undefined = undefined,
  ) {
    super(_id, _name, _email, _phone);
  }
}

export class Phase extends ItemNameAndId {
  public static parse(proto: Phase_Read): Phase {
    return new Phase(
      proto.id ?? '',
      proto.name ?? '',
      proto.code ?? '',
      proto.primary ?? false,
      undefined,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
  public static deparseUpdate(phase: Phase): Phase_Update {
    return {
      id: phase.id === '' ? undefined : phase.id,
      name: phase.name || '',
      code: phase.code || undefined,
      primary: phase.primary || false,
      _destroy: phase._destroy || undefined,
    };
  }
  public static deparse(phase: {
    name?: string | null;
    code?: string | null;
    primary?: boolean | null;
  }): Phase_Create {
    return {
      name: phase?.name || '',
      code: phase?.code || undefined,
      primary: phase?.primary || false,
    };
  }
  public get code(): string | undefined {
    return this._code;
  }
  public set code(value: string | undefined) {
    this._code = value;
  }
  public get primary(): boolean | undefined {
    return this._primary;
  }
  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    _id: string,
    _name: string,
    private _code: string | undefined,
    private _primary: boolean | undefined,
    private _destroy: number | undefined = undefined,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {
    super(_name, _id);
  }
}

export class PhaseBasic extends ItemNameAndId {
  public static parse(proto: Phase_Read_Typeahead): PhaseBasic {
    return new PhaseBasic(
      proto.id ?? '',
      proto.name ?? '',
      proto.code ?? '',
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public get code() {
    return this._code;
  }
  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    _id: string,
    _name: string,
    private _code: string | null,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {
    super(_name, _id);
  }
}

export class ProjectBasic extends ItemNameAndId {
  public static parse(proto: Project_Read_Nested): ProjectBasic {
    return new ProjectBasic(
      proto.id ?? '',
      proto.name ?? '',
      proto.external_id ?? '',
      proto.notes ?? '',
      proto.order_notes ?? null,
      proto.job_notes ?? null,
      proto.internal_notes ?? null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
  public get externalId(): string {
    return this._external_id;
  }
  public get notes(): string {
    return this._notes;
  }
  public get orderNotes(): Nullable<string> {
    return this._order_notes;
  }
  public get jobNotes(): Nullable<string> {
    return this._job_notes;
  }
  public get internalNotes(): Nullable<string> {
    return this._internal_notes;
  }
  public get discardedAt() {
    return this._discarded_at;
  }
  constructor(
    _id: string,
    _name: string,
    private _external_id: string,
    private _notes: string,
    private _order_notes: Nullable<string>,
    private _job_notes: Nullable<string>,
    private _internal_notes: Nullable<string>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {
    super(_name, _id);
  }
}
export class ProjectMaterialTotal {
  public static parse(proto: ProjectMaterialTotal_Read): ProjectMaterialTotal {
    return new ProjectMaterialTotal(
      proto.id ?? '',
      proto.total_quantity ?? '',
      proto.unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.unit_of_measure,
            id: proto.unit_of_measure,
          })
        : null,
      proto.material ? BasicMaterial.parse(proto.material) : null,
      proto.phase ? Phase.parse(proto.phase) : null,
      proto.site ? SiteBasic.parse(proto.site) : null,
    );
  }
  public get material(): Nullable<BasicMaterial> {
    return this._material;
  }
  public set material(value: Nullable<BasicMaterial>) {
    this._material = value;
  }
  public get unitOfMeasure(): Nullable<ItemNameAndId> {
    return this._unit_of_measure;
  }
  public set unitOfMeasure(value: Nullable<ItemNameAndId>) {
    this._unit_of_measure = value;
  }
  public get totalQuantity(): string {
    return this._total_quantity;
  }
  public set totalQuantity(value: string) {
    this._total_quantity = value;
  }
  public get id(): string {
    return this._id;
  }
  public get phase(): Nullable<Phase> {
    return this._phase;
  }
  public set phase(value: Nullable<Phase>) {
    this._phase = value;
  }
  public get site(): Nullable<SiteBasic> {
    return this._site;
  }
  public set site(value: Nullable<SiteBasic>) {
    this._site = value;
  }
  constructor(
    private _id: string,
    private _total_quantity: string,
    private _unit_of_measure: Nullable<ItemNameAndId>,
    private _material: Nullable<BasicMaterial>,
    private _phase: Nullable<Phase> = null,
    private _site: Nullable<SiteBasic> = null,
  ) {}
}
interface ContactTypeMap {
  [key: string]: ContactTypes;
}

export const contactTypeMap: ContactTypeMap = {
  foremanContact: ContactTypes.FOREMAN,
  salesContact: ContactTypes.SALES,
  collaboratorContact: ContactTypes.COLLABORATOR,
  supervisorContact: ContactTypes.SUPERVISOR,
};

export const filterContact = (
  project: Project | any,
  contactType: ContactTypes,
): ExtendedContactItem | null => {
  return (
    project?.allContacts
      ?.filter((contact: ExtendedContactItem) => contact?.type?.id === contactType)
      ?.find(Boolean) || null
  );
};

export const transformContact = (
  contact: ExtendedContactItem | null | any,
  isUpdate: boolean = false,
) => {
  if (contact?.isDeleted) {
    // for create operations, omit deleted contacts entirely
    if (!isUpdate) {
      return null;
    }

    // for update operations, mark as deleted
    return { id: contact.id, _destroy: 1 };
  }
  if (!contact) return null;
  return ContactItem.deparse(contact);
};

export class ProjectExternalIdTypeahead {
  constructor(private _external_id: string | null) {}

  public get externalId() {
    return this._external_id;
  }

  public static parse(proto: Project_Read_Typeahead_External_Id) {
    return new ProjectExternalIdTypeahead(proto.external_id ?? null);
  }
}

export class ProjectTypeahead {
  constructor(
    public _id: string,
    public _name: string,
    public _external_id: string | null,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get externalId() {
    return this._external_id;
  }

  public static parse(proto: Project_Read_Typeahead) {
    return new ProjectTypeahead(proto.id, proto.name, proto.external_id);
  }
}

export class ProjectSummaryBasic {
  constructor(
    private _orders_count: number,
    private _total_tickets_count: number,
    private _total_quantity: number,
    private _total_quantity_unit_of_measure: Nullable<ItemNameAndId>,
    private _completed_in_review_jobs_count: number,
  ) {}

  public static parse(proto: ProjectSummary_Read_Nested) {
    return new ProjectSummaryBasic(
      proto.orders_count,
      proto.total_tickets_count,
      Number(proto.total_quantity),
      proto.total_quantity_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.total_quantity_unit_of_measure,
            id: proto.total_quantity_unit_of_measure,
          })
        : null,
      proto.completed_in_review_jobs_count,
    );
  }

  public get ordersCount() {
    return this._orders_count;
  }

  public get totalTicketsCount() {
    return this._total_tickets_count;
  }

  public get totalQuantity() {
    return this._total_quantity;
  }

  public get totalQuantityUnitOfMeasure() {
    return this._total_quantity_unit_of_measure;
  }

  public get completedInReviewJobsCount() {
    return this._completed_in_review_jobs_count;
  }
}

export class Project extends ProjectBasic {
  public static parse(proto: Project_Read): Project {
    return new Project(
      proto.id ?? '',
      proto.name ?? '',
      proto.notes ?? '',
      proto.created_at ?? '',
      proto.updated_at ?? '',
      proto.expires_at ? dayjs(proto.expires_at) : null,
      proto.effective_at ? dayjs(proto.effective_at) : null,
      proto.po_job_number ?? '',
      proto.external_id ?? '',
      proto.project_id ?? '',
      proto.company ? CompanyBasic.parse(proto.company) : null,
      proto.state ?? null,
      proto.contact ? ContactItem.parse(proto.contact) : null,
      proto.account ? AccountBasic.parse(proto.account) : null,
      proto.site ? SiteBasic.parse(proto.site) : null,
      proto.phases ? proto.phases.map((item) => Phase.parse(item)) : [],
      proto.project_material_totals
        ? proto.project_material_totals.map((item) => ProjectMaterialTotal.parse(item))
        : [],
      proto.sales_contact
        ? ExtendedContactItem.parse({ ...proto.sales_contact, type: ContactTypes.SALES })
        : null,
      proto.foreman_contact
        ? ExtendedContactItem.parse({
            ...proto.foreman_contact,
            type: ContactTypes.FOREMAN,
          })
        : null,
      proto.collaborator_contact
        ? ExtendedContactItem.parse({
            ...proto.collaborator_contact,
            type: ContactTypes.COLLABORATOR,
          })
        : null,
      proto.supervisor_contact
        ? ExtendedContactItem.parse({
            ...proto.supervisor_contact,
            type: ContactTypes.SUPERVISOR,
          })
        : null,
      proto.department ? BasicDepartment.parse(proto.department) : null,
      proto.foremen?.length
        ? proto?.foremen.map((item) => User.parse(item as User_Read))
        : [],
      proto.hauler_rate_type ? (proto.hauler_rate_type as RateType) : null,
      proto.service ? Service.parse(proto.service) : null,
      proto.service_class ? BasicServiceClass.parse(proto.service_class) : null,
      proto.job_time ?? null,
      proto.order_quantity ?? null,
      proto.order_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.order_unit_of_measure,
            id: proto.order_unit_of_measure,
          })
        : null,
      proto.equipment_type ? EquipmentTypeBasic.parse(proto.equipment_type) : null,
      parseFiniteNumber(proto.equipment_type_gross_capacity),
      proto.equipment_type_gross_capacity_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.equipment_type_gross_capacity_unit_of_measure,
            id: proto.equipment_type_gross_capacity_unit_of_measure,
          })
        : null,
      proto.loads_per_truck ?? null,
      proto.truck_count ?? null,
      proto.units_per_hour ?? null,
      proto.job_quantity ?? null,
      proto.job_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.job_unit_of_measure,
            id: proto.job_unit_of_measure,
          })
        : null,
      proto.order_notes ?? '',
      proto.job_notes ?? '',
      proto.internal_notes ?? '',
      proto.waypoints.length ? proto.waypoints.map(WayPoint.parse) : null,
      proto.waypoints.length
        ? proto.waypoints
            .filter((point) => {
              return point.type === WaypointType.PICKUP;
            })
            .map(WayPoint.parse)
        : null,
      proto.waypoints.length
        ? proto.waypoints
            .filter((point) => {
              return point.type === WaypointType.DROP_OFF;
            })
            .map(WayPoint.parse)
        : null,
      proto.waypoints.length
        ? proto.waypoints
            .filter((point) => {
              return point.type === WaypointType.STAGING;
            })
            .map(WayPoint.parse)
        : null,
      proto.customer_rate ? RateBasicWithValue.parse(proto.customer_rate) : null,
      parseFiniteNumber(proto.customer_rate_value),
      proto.customer_rate_type,
      proto.vendor_rate ? RateBasicWithValue.parse(proto.vendor_rate) : null,
      parseFiniteNumber(proto.vendor_rate_value),
      proto.vendor_rate_type,
      proto.driver_rate ? RateBasicWithValue.parse(proto.driver_rate) : null,
      parseFiniteNumber(proto.driver_rate_value),
      proto.driver_rate_type,
      proto.requester ? UserBasic.parse(proto.requester) : null,
      (proto.collaborators ?? []).map((user) => UserBasic.parse(user)),
      proto.dispatch_number ? ItemNameAndId.parse(proto.dispatch_number) : null,
      proto.contract_number ?? null,
      proto.load_unit_of_measure
        ? ItemNameAndId.parse({
            name: proto.load_unit_of_measure,
            id: proto.load_unit_of_measure,
          })
        : null,
      isNil(proto.recurring_loads) ? null : proto.recurring_loads,
      proto.charge_to_account ?? null,
      isNil(proto.chase_missing_tickets) ? null : proto.chase_missing_tickets,
      proto.default_digital_ticket_view ?? null,
      proto.prevailing_wage ?? false,
      proto.material_rate_itemized ?? false,
      isNil(proto.tax_rate) ? null : Number(proto.tax_rate),
      isNil(proto.tax_freight) ? null : proto.tax_freight,
      isNil(proto.tax_material) ? null : proto.tax_material,
      isNil(proto.tax_add_ons) ? null : proto.tax_add_ons,
      proto.tax_rate_schedule
        ? TaxRateScheduleBasic.parse(proto.tax_rate_schedule)
        : null,
      proto.salespeople?.length
        ? proto?.salespeople.map((item) => User.parse(item as User_Read))
        : [],
      proto.state_events ?? [],
      proto.project_terms ?? null,
      proto.effective_project_terms ?? null,
      proto.project_summary ? ProjectSummaryBasic.parse(proto.project_summary) : null,
      // TODO(TRE-15586): replace with api-spec type after BE merges (field not yet in Project_Read)
      (proto as unknown as { apply_rate_changes_to_existing_orders?: boolean })
        .apply_rate_changes_to_existing_orders ?? false,
      proto.live_eta ?? false,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.vendor_pay_commission_percentage ?? null,
      proto.driver_pay_commission_percentage ?? null,
    );
  }
  public get phases(): Phase[] {
    return this._phases;
  }
  public set phases(value: Phase[]) {
    this._phases = value;
  }
  public get projectMaterialTotals(): ProjectMaterialTotal[] {
    return this._project_material_totals;
  }

  public get projectSummary() {
    return this._project_summary;
  }
  public set projectMaterialTotals(value: ProjectMaterialTotal[]) {
    this._project_material_totals = value;
  }
  public get salesContact(): Nullable<ExtendedContactItem> {
    return this._sales_contact;
  }
  public set salesContact(value: Nullable<ExtendedContactItem>) {
    this._sales_contact = value;
  }
  public get foremanContact(): Nullable<ExtendedContactItem> {
    return this._foreman_contact;
  }
  public set foremanContact(value: Nullable<ExtendedContactItem>) {
    this._foreman_contact = value;
  }
  public get collaboratorContact(): Nullable<ExtendedContactItem> {
    return this._collaborator_contact;
  }
  public set collaboratorContact(value: Nullable<ExtendedContactItem>) {
    this._collaborator_contact = value;
  }
  public get supervisorContact(): Nullable<ExtendedContactItem> {
    return this._supervisor_contact;
  }
  public set supervisorContact(value: Nullable<ExtendedContactItem>) {
    this._supervisor_contact = value;
  }
  public get allContacts(): ExtendedContactItem[] {
    const allContacts = [];
    if (this._sales_contact) {
      allContacts.push(this._sales_contact);
    }
    if (this._foreman_contact) {
      allContacts.push(this._foreman_contact);
    }
    if (this._collaborator_contact) {
      allContacts.push(this._collaborator_contact);
    }
    if (this._supervisor_contact) {
      allContacts.push(this._supervisor_contact);
    }
    return allContacts;
  }

  // Public set allContacts(value: any) {
  //   This._sales_contact = value.filter((item: any) => item.type === 'sales')[0];
  //   This._foreman_contact = value.filter((item: any) => item.type === 'sales')[0];
  //   This._collaborator_contact = value.filter((item: any) => item.type === 'sales')[0];
  // }
  public get createdAt(): string {
    return this._created_at;
  }
  public get updatedAt(): string {
    return this._updated_at;
  }
  public get expiresAt(): Nullable<Dayjs> {
    return this._expires_at;
  }
  public get effectiveAt(): Nullable<Dayjs> {
    return this._effective_at;
  }
  public get poJobNumber(): string {
    return this._po_job_number;
  }
  public get projectId(): string {
    return this._project_id;
  }
  public get company(): Nullable<CompanyBasic> {
    return this._company;
  }
  public get isSalesContact(): boolean {
    return Boolean(this._sales_contact?.id.length);
  }
  public get isForemanContact(): boolean {
    return Boolean(this._foreman_contact?.id.length);
  }
  public get isSupervisorContact(): boolean {
    return Boolean(this._supervisor_contact?.id.length);
  }
  public get isCollaboratorContact(): boolean {
    return Boolean(this._collaborator_contact?.id.length);
  }
  public get isActive(): boolean {
    return this._state === ProjectState.ACTIVE;
  }
  public get state(): Nullable<ProjectState> {
    return this._state;
  }
  public get stateEvents(): ProjectStateEvent[] {
    return this._state_events;
  }
  public get contact(): Nullable<ContactItem> {
    return this._contact;
  }
  public get account(): Nullable<AccountBasic> {
    return this._account;
  }
  public get site(): Nullable<SiteBasic> {
    return this._site;
  }
  public get department(): Nullable<BasicDepartment> {
    return this._department;
  }
  public get foremen(): Array<User> {
    return this._foremen || [];
  }
  public get collaborators() {
    return this._collaborators;
  }
  public get salespeople() {
    return this._salespeople || [];
  }
  public get haulerRateType(): Nullable<RateType> {
    return this._hauler_rate_type;
  }
  public get service(): Nullable<Service> {
    return this._service;
  }
  public get serviceClass(): Nullable<BasicServiceClass> {
    return this._service_class;
  }
  public get jobTime(): Nullable<number> {
    return this._job_time;
  }
  public get equipmentType(): Nullable<EquipmentTypeBasic> {
    return this._equipment_type;
  }
  public get equipmentTypeGrossCapacity() {
    return this._equipment_type_gross_capacity;
  }
  public get loadsPerTruck(): Nullable<number> {
    return this._loads_per_truck;
  }
  public get truckCount(): Nullable<number> {
    return this._truck_count;
  }
  public get unitsPerHour(): Nullable<number> {
    return this._units_per_hour;
  }
  public get jobQuantity(): Nullable<string> {
    return this._job_quantity;
  }
  public get orderQuantity(): Nullable<string> {
    return this._order_quantity;
  }
  public get projectNotes(): string {
    return this.notes;
  }
  public get waypoints(): Nullable<WayPoint[]> {
    return this._waypoints;
  }
  public set pickUpWayPoints(waypoints: Nullable<WayPoint[]>) {
    this._waypoints = cloneDeep(waypoints);
  }
  public get pickUpWayPoints(): Nullable<WayPoint[]> {
    return this._pickUpWayPoints;
  }
  public get dropOffWayPoints(): Nullable<WayPoint[]> {
    return this._dropOffWayPoints;
  }
  public set dropOffWayPoints(waypoints: Nullable<WayPoint[]>) {
    this._waypoints = cloneDeep(waypoints);
  }
  public get stagingWayPoints(): Nullable<WayPoint[]> {
    return this._stagingWayPoints;
  }
  public set stagingWayPoints(waypoints: Nullable<WayPoint[]>) {
    this._stagingWayPoints = cloneDeep(waypoints);
  }
  public get customerRate() {
    return this._customer_rate;
  }

  public get customerRateValue() {
    return this._customer_rate_value;
  }

  public get customerRateType() {
    return this._customer_rate_type;
  }

  public get vendorRate() {
    return this._vendor_rate;
  }

  public get vendorPayCommissionPercentage() {
    return this._vendor_pay_commission_percentage;
  }
  public get driverPayCommissionPercentage() {
    return this._driver_pay_commission_percentage;
  }

  public get vendorRateValue() {
    return this._vendor_rate_value;
  }

  public get vendorRateType() {
    return this._vendor_rate_type;
  }

  public get driverRate() {
    return this._driver_rate;
  }

  public get driverRateValue() {
    return this._driver_rate_value;
  }

  public get driverRateType() {
    return this._driver_rate_type;
  }

  public get requester() {
    return this._requester;
  }

  public get dispatchNumber() {
    return this._dispatch_number;
  }

  public get contractNumber() {
    return this._contract_number;
  }

  public get loadUnitOfMeasure() {
    return this._load_unit_of_measure;
  }

  public get orderUnitOfMeasure() {
    return this._order_unit_of_measure;
  }

  public get jobUnitOfMeasure() {
    return this._job_unit_of_measure;
  }

  public get equipmentTypeGrossCapacityUnitOfMeasure() {
    return this._equipment_type_gross_capacity_unit_of_measure;
  }

  public get recurringLoads() {
    return this._recurring_loads;
  }

  public get chargeToAccount() {
    return this._charge_to_account;
  }

  public get chaseMissingTickets() {
    return this._chase_missing_tickets;
  }

  public get defaultDigitalTicketView() {
    return this._default_digital_ticket_view;
  }

  public get prevailingWage() {
    return this._prevailing_wage;
  }

  public get applyRateChangesToExistingOrders() {
    return this._apply_rate_changes_to_existing_orders;
  }

  public get liveEta() {
    return this._live_eta;
  }

  public get materialRateItemized() {
    return this._material_rate_itemized;
  }

  public get taxRate() {
    return this._tax_rate;
  }

  public get taxFreight() {
    return this._tax_freight;
  }

  public get taxMaterial() {
    return this._tax_material;
  }

  public get taxAddOns() {
    return this._tax_add_ons;
  }

  public get taxRateSchedule() {
    return this._tax_rate_schedule;
  }

  public get projectTerms(): Nullable<string> {
    return this._project_terms;
  }

  // BE-computed: project_terms when set, else company.default_project_terms, else null.
  // Renderers (Quote PDF, etc.) should read this rather than recomputing the precedence
  // client-side.
  public get effectiveProjectTerms(): Nullable<string> {
    return this._effective_project_terms;
  }

  constructor(
    _id: string,
    _name: string,
    _notes: string,
    private _created_at: string,
    private _updated_at: string,
    private _expires_at: Nullable<Dayjs>,
    private _effective_at: Nullable<Dayjs>,
    private _po_job_number: string,
    _external_id: string,
    private _project_id: string,
    private _company: Nullable<CompanyBasic>,
    private _state: Nullable<ProjectState>,
    private _contact: Nullable<ContactItem>,
    private _account: Nullable<AccountBasic>,
    private _site: Nullable<SiteBasic>,
    private _phases: Phase[],
    private _project_material_totals: ProjectMaterialTotal[],
    private _sales_contact: Nullable<ExtendedContactItem>,
    private _foreman_contact: Nullable<ExtendedContactItem>,
    private _collaborator_contact: Nullable<ExtendedContactItem>,
    private _supervisor_contact: Nullable<ExtendedContactItem>,
    private _department: Nullable<BasicDepartment>,
    private _foremen: Array<User> | null,
    private _hauler_rate_type: Nullable<RateType>,
    private _service: Nullable<Service>,
    private _service_class: Nullable<BasicServiceClass>,
    private _job_time: Nullable<number>,
    private _order_quantity: Nullable<string>,
    private _order_unit_of_measure: Nullable<ItemNameAndId>,
    private _equipment_type: Nullable<EquipmentTypeBasic>,
    private _equipment_type_gross_capacity: Nullable<number>,
    private _equipment_type_gross_capacity_unit_of_measure: Nullable<ItemNameAndId>,
    private _loads_per_truck: Nullable<number>,
    private _truck_count: Nullable<number>,
    private _units_per_hour: Nullable<number>,
    private _job_quantity: Nullable<string>,
    private _job_unit_of_measure: Nullable<ItemNameAndId>,
    _order_notes: Nullable<string>,
    _job_notes: Nullable<string>,
    _internal_notes: Nullable<string>,
    private _waypoints: Nullable<WayPoint[]>,
    private _pickUpWayPoints: Nullable<WayPoint[]>,
    private _dropOffWayPoints: Nullable<WayPoint[]>,
    private _stagingWayPoints: Nullable<WayPoint[]>,
    private _customer_rate: Nullable<RateBasicWithValue>,
    private _customer_rate_value: Nullable<number>,
    private _customer_rate_type: Nullable<RateType>,
    private _vendor_rate: Nullable<RateBasicWithValue>,
    private _vendor_rate_value: Nullable<number>,
    private _vendor_rate_type: Nullable<RateType>,
    private _driver_rate: Nullable<RateBasicWithValue>,
    private _driver_rate_value: Nullable<number>,
    private _driver_rate_type: Nullable<RateType>,
    private _requester: Nullable<UserBasic>,
    private _collaborators: Array<UserBasic>,
    private _dispatch_number: Nullable<ItemNameAndId>,
    private _contract_number: Nullable<string>,
    private _load_unit_of_measure: Nullable<ItemNameAndId>,
    private _recurring_loads: Nullable<boolean>,
    private _charge_to_account: Nullable<string>,
    private _chase_missing_tickets: Nullable<boolean>,
    private _default_digital_ticket_view: Nullable<boolean>,
    private _prevailing_wage: boolean,
    private _material_rate_itemized: boolean,
    private _tax_rate: Nullable<number>,
    private _tax_freight: Nullable<boolean>,
    private _tax_material: Nullable<boolean>,
    private _tax_add_ons: Nullable<boolean>,
    private _tax_rate_schedule: Nullable<TaxRateScheduleBasic>,
    private _salespeople: Array<User> | null,
    private _state_events: ProjectStateEvent[],
    private _project_terms: Nullable<string>,
    private _effective_project_terms: Nullable<string>,
    private _project_summary: Nullable<ProjectSummaryBasic> = null,
    // Trailing + defaulted so existing positional `new Project(...)` callers are unaffected
    // (selective-rate-apply toggle; not yet in the api-spec — see parse/deparse temp-casts).
    private _apply_rate_changes_to_existing_orders: boolean = false,
    // Dispatch Live ETA opt-in; cascades server-side Project → Order → Job (TRE-15771).
    private _live_eta: boolean = false,
    _discarded_at: Nullable<Dayjs> = null,
    private _vendor_pay_commission_percentage: NonNullable<
      Project_Read['vendor_pay_commission_percentage']
    > | null = null,
    private _driver_pay_commission_percentage: NonNullable<
      Project_Read['driver_pay_commission_percentage']
    > | null = null,
  ) {
    super(
      _id,
      _name,
      _external_id,
      _notes,
      _order_notes,
      _job_notes,
      _internal_notes,
      _discarded_at,
    );
  }
}

// ============================================================================
// V2 deparse functions (project-form-v2-17).
//
// `deparseCreateV2` and `deparseUpdateV2` are the data-correctness core of the
// rework:
//
//  1. `deparseCreateV2(formData): Project_Create` — full payload, fully typed.
//  2. `deparseUpdateV2(formData, dirtyFields): Partial<Project_Update>` — sparse
//     PATCH: emits ONLY fields where `dirtyFields[key]` is truthy. Backed by a
//     per-field deparser map with a compile-time `satisfies` exhaustiveness
//     gate — adding a key to `ProjectFormSchemaInterface` without a deparser
//     entry is a TypeScript error, so a new schema field cannot silently skip
//     the save path.
//
// CRITICAL: schema keys are `effectiveAt` / `expiresAt` (camelCase). BE keys
// are `effective_at` / `expires_at`. Using the placeholder names from the
// original plan (the start/end pair) was the exact root cause of the
// 05-20-26 date-loss regression in production — always map through the
// correct camelCase form keys to the correct BE snake_case keys.
// ============================================================================

export type ProjectFormDirtyFields = Partial<
  Record<keyof ProjectFormSchemaInterface, unknown>
>;

// Schema-declared `allContacts` items don't carry an `id` (yup shape omits it),
// but the form stores one at runtime via `EmptyContactItem`. Narrow structurally
// here so the deparser can read `id` without tripping the v2 grep gate on
// `as any` / `as unknown`.
type AllContactsItemRuntime = NonNullable<
  ProjectFormSchemaInterface['allContacts']
>[number] & { id?: string };

// Pick the `allContacts` item whose `type.id` matches the requested contact
// type. Mirrors `filterContact` but reads off raw form data — the legacy helper
// expects a `Project | any` object whereas the V2 caller has the typed form.
function pickAllContactByType(
  formData: ProjectFormSchemaInterface,
  contactType: ContactTypes,
): AllContactsItemRuntime | undefined {
  const contacts = (formData.allContacts ?? []) as ReadonlyArray<AllContactsItemRuntime>;
  return contacts.find((c) => c?.type?.id === contactType);
}

// Build a `Contact_Update_Destroyable` (id + name + phone + optional _destroy).
function buildContactUpdate(
  contact: AllContactsItemRuntime | undefined,
): Contact_Update_Destroyable | undefined {
  if (!contact) return undefined;
  if (contact.isDeleted) {
    if (!contact.id) return undefined; // never saved → omit entirely
    return { id: contact.id, _destroy: 1 };
  }
  const name = contact.name?.trim();
  const phone = contact.phone?.trim();
  const result: Contact_Update_Destroyable = {
    ...(contact.id ? { id: contact.id } : {}),
    name: name?.length ? name : undefined,
    phone: phone?.length ? phone.replace(/[^+\d]/g, '') : undefined,
  };
  return result;
}

// Build a `Contact_Create` (no id, no _destroy). Deleted contacts are dropped
// entirely from create payloads.
function buildContactCreate(
  contact: AllContactsItemRuntime | undefined,
): Contact_Create | undefined {
  if (!contact || contact.isDeleted) return undefined;
  const name = contact.name?.trim();
  const phone = contact.phone?.trim();
  return {
    name: name?.length ? name : undefined,
    phone: phone?.length ? phone.replace(/[^+\d]/g, '') : undefined,
  };
}

type ContactSlots = {
  sales_contact?: Contact_Update_Destroyable;
  foreman_contact?: Contact_Update_Destroyable;
  collaborator_contact?: Contact_Update_Destroyable;
  supervisor_contact?: Contact_Update_Destroyable;
};

function buildAllContactsForUpdate(formData: ProjectFormSchemaInterface): ContactSlots {
  const result: ContactSlots = {};
  const sales = buildContactUpdate(pickAllContactByType(formData, ContactTypes.SALES));
  const foreman = buildContactUpdate(
    pickAllContactByType(formData, ContactTypes.FOREMAN),
  );
  const collaborator = buildContactUpdate(
    pickAllContactByType(formData, ContactTypes.COLLABORATOR),
  );
  const supervisor = buildContactUpdate(
    pickAllContactByType(formData, ContactTypes.SUPERVISOR),
  );
  if (sales) result.sales_contact = sales;
  if (foreman) result.foreman_contact = foreman;
  if (collaborator) result.collaborator_contact = collaborator;
  if (supervisor) result.supervisor_contact = supervisor;
  return result;
}

type ContactCreateSlots = {
  sales_contact?: Contact_Create;
  foreman_contact?: Contact_Create;
  collaborator_contact?: Contact_Create;
  supervisor_contact?: Contact_Create;
};

function buildAllContactsForCreate(
  formData: ProjectFormSchemaInterface,
): ContactCreateSlots {
  const result: ContactCreateSlots = {};
  const sales = buildContactCreate(pickAllContactByType(formData, ContactTypes.SALES));
  const foreman = buildContactCreate(
    pickAllContactByType(formData, ContactTypes.FOREMAN),
  );
  const collaborator = buildContactCreate(
    pickAllContactByType(formData, ContactTypes.COLLABORATOR),
  );
  const supervisor = buildContactCreate(
    pickAllContactByType(formData, ContactTypes.SUPERVISOR),
  );
  if (sales) result.sales_contact = sales;
  if (foreman) result.foreman_contact = foreman;
  if (collaborator) result.collaborator_contact = collaborator;
  if (supervisor) result.supervisor_contact = supervisor;
  return result;
}

// Build BE waypoints array for an UPDATE. A saved waypoint (id present) with
// no site -> soft-delete via `_destroy: 1`. A non-saved slot with no site is
// dropped. The combined return covers all 3 schema slots so any dirty-waypoint
// deparser entry produces the full payload (BE replaces the whole array).
// Strip every char except `+` and digits from the phone — matches the
// allContacts deparser at `buildContactUpdate`. The
// PhoneCodeFlagInput surfaces values like "+1 0005551234" (with a literal
// space between country code and number); the BE rejects anything that
// isn't a valid E.164-style string. Matches the WayPoint.deparse phone
// strip rule the legacy create path still uses.
const sanitizePhone = (raw: string | null | undefined): string =>
  (raw ?? '').replace(/[^+\d]/g, '');

const buildWaypointContactPayload = (
  contact: { name?: string | null; phone?: string | null } | undefined,
): { name: string; phone: string } => ({
  name: contact?.name?.trim() ?? '',
  phone: sanitizePhone(contact?.phone),
});

function buildWaypointsForUpdate(
  formData: ProjectFormSchemaInterface,
): Waypoint_Update_Destroyable[] {
  const slots: ReadonlyArray<{
    form: ProjectFormSchemaInterface['pickUpWayPoint'];
    type: WaypointType;
  }> = [
    { form: formData.pickUpWayPoint, type: WaypointType.PICKUP },
    { form: formData.dropOffWayPoint, type: WaypointType.DROP_OFF },
    { form: formData.stagingWayPoint, type: WaypointType.STAGING },
  ];
  const result: Waypoint_Update_Destroyable[] = [];
  for (const { form, type } of slots) {
    if (!form) continue;
    const siteId = form.site?.id;
    if (siteId) {
      const contact = form.contact ?? undefined;
      const hasContactValue =
        !!contact && Object.values(contact).some((v) => v !== undefined && v !== '');
      // For an already-persisted waypoint (form.id present), always emit
      // the contact field — even when empty. The BE treats an absent
      // contact key as "no change" and an explicit `{ name: '', phone: '' }`
      // as "clear"; this is the only way the user's "Remove Pick Up Site
      // Contact" action lands on the server. Brand-new waypoints (no id
      // yet) drop the field when empty so a never-touched contact stays
      // out of the create payload.
      const shouldEmitEmptyContact = Boolean(form.id);
      result.push({
        ...(form.id ? { id: form.id } : {}),
        type,
        site_id: siteId,
        ordinality: form.ordinality ?? 10,
        ...(hasContactValue || shouldEmitEmptyContact
          ? { contact: buildWaypointContactPayload(contact) }
          : {}),
      });
    } else if (form.id) {
      result.push({ id: form.id, _destroy: 1 });
    }
  }
  return result;
}

// Build BE waypoints array for a CREATE. Slots without a site_id are dropped.
function buildWaypointsForCreate(
  formData: ProjectFormSchemaInterface,
): Waypoint_Create[] {
  const slots: ReadonlyArray<{
    form: ProjectFormSchemaInterface['pickUpWayPoint'];
    type: WaypointType;
  }> = [
    { form: formData.pickUpWayPoint, type: WaypointType.PICKUP },
    { form: formData.dropOffWayPoint, type: WaypointType.DROP_OFF },
    { form: formData.stagingWayPoint, type: WaypointType.STAGING },
  ];
  const result: Waypoint_Create[] = [];
  for (const { form, type } of slots) {
    if (!form?.site?.id) continue;
    const contact = form.contact ?? undefined;
    const hasContactValue =
      !!contact && Object.values(contact).some((v) => v !== undefined && v !== '');
    result.push({
      type,
      site_id: form.site.id,
      ordinality: form.ordinality ?? 10,
      ...(hasContactValue ? { contact: buildWaypointContactPayload(contact) } : {}),
    });
  }
  return result;
}

function buildPhasesForUpdate(formData: ProjectFormSchemaInterface): Phase_Update[] {
  if (!formData.phases?.length) return [];
  const isTempId = (id: string | undefined): boolean => !!id && id.startsWith('_temp_');
  return formData.phases
    .filter((phase) => !(phase.isDeleted && isTempId(phase.id)))
    .map<Phase_Update>((phase) =>
      phase.isDeleted
        ? { id: phase.id, _destroy: 1 }
        : {
            ...(phase.id && !isTempId(phase.id) ? { id: phase.id } : {}),
            name: phase.name ?? '',
            ...(phase.code ? { code: phase.code } : {}),
            primary: phase.primary ?? false,
          },
    );
}

function buildPhasesForCreate(formData: ProjectFormSchemaInterface): Phase_Create[] {
  if (!formData.phases?.length) return [];
  return formData.phases
    .filter((phase) => !phase.isDeleted)
    .map<Phase_Create>((phase) => ({
      name: phase.name ?? '',
      ...(phase.code ? { code: phase.code } : {}),
      primary: phase.primary ?? false,
    }));
}

// Phase rows added in the V2 PhaseDrawer carry a `_temp_<uuid>` placeholder
// id so React can track them before the BE assigns a real UUID. Material
// totals reference a phase by id, so if a row points at a not-yet-persisted
// phase we must omit phase_id and let the BE resolve it from phase_name +
// phase_code. Leaking `_temp_*` into phase_id trips the Destroyable.id
// UUID-format validator on create AND update.
const resolvePhaseIdForBE = (phaseId: string | undefined | null): string | null =>
  !phaseId || phaseId.startsWith('_temp_') ? null : phaseId;

function buildProjectMaterialTotalsForUpdate(
  formData: ProjectFormSchemaInterface,
): ProjectMaterialTotal_Update[] {
  const rows = formData.projectMaterialTotals ?? [];
  // Mirror the legacy form: deparse only rows with a filled-in
  // material. The default first row is empty, so an unfilled initial row is
  // "not yet filled in" and dropped. Filtering EACH row (not just guarding on
  // rows[0]) ensures a filled later row survives when the first row is left
  // blank — guarding on rows[0] alone silently dropped the whole array.
  // Deleted rows keep their material id, so `_destroy` still emits for them.
  const filledRows = rows.filter((row) => Boolean(row?.material?.id));
  if (!filledRows.length) return [];
  return filledRows.map<ProjectMaterialTotal_Update>((row) => {
    if (row?.isDeleted) {
      return { ...(row.id ? { id: row.id } : {}), _destroy: 1 };
    }
    const uomId = row?.unitOfMeasure?.id ?? null;
    return {
      ...(row?.id ? { id: row.id } : {}),
      material_id: row?.material?.id ?? '',
      total_quantity:
        isNil(row?.totalQuantity) || !String(row?.totalQuantity).length
          ? null
          : Number(row?.totalQuantity),
      unit_of_measure: uomId ? (uomId as OrderUnitOfMeasure) : null,
      phase_id: resolvePhaseIdForBE(row?.phase?.id),
      phase_name: row?.phase?.name || null,
      phase_code: row?.phase?.code || null,
      site_id: row?.site?.id || null,
    };
  });
}

function buildProjectMaterialTotalsForCreate(
  formData: ProjectFormSchemaInterface,
): ProjectMaterialTotal_Create[] | undefined {
  const rows = formData.projectMaterialTotals ?? [];
  // Mirror the legacy form: keep only rows with a filled-in
  // material. Filtering each row (not just guarding on rows[0]) ensures a
  // filled later row survives when the default first row is left blank.
  const filledRows = rows.filter((row) => Boolean(row?.material?.id));
  if (!filledRows.length) return undefined;
  return filledRows.map<ProjectMaterialTotal_Create>((row) => ({
    material_id: row?.material?.id ?? '',
    unit_of_measure: row?.unitOfMeasure?.id
      ? (row.unitOfMeasure.id as OrderUnitOfMeasure)
      : null,
    total_quantity: parseFiniteNumber(row?.totalQuantity),
    phase_id: resolvePhaseIdForBE(row?.phase?.id),
    phase_name: row?.phase?.name || null,
    phase_code: row?.phase?.code || null,
    site_id: row?.site?.id || null,
  }));
}

// `dispatch_number` payload mirrors the legacy ProjectForm deparser:
//   - Non-empty string → { name }
//   - Empty string on a saved project → soft-delete the prior value
//   - Otherwise → omit entirely
function buildDispatchNumberForUpdate(
  formData: ProjectFormSchemaInterface,
): { dispatch_number: DispatchNumber_Update } | Record<string, never> {
  const raw = formData.dispatch_number;
  if (raw && raw.trim() !== '') {
    return { dispatch_number: { name: raw } };
  }
  if (raw === '' && formData.id) {
    return { dispatch_number: { _destroy: 1 } };
  }
  return {};
}

// Narrow the per-row UoM read used by `order_unit_of_measure` /
// `job_unit_of_measure` / `equipment_type_gross_capacity_unit_of_measure`.
// Yup `.when()` collapses InferType to `{}` because the conditional branches
// can disagree on shape (see Codebase Patterns); structural narrow to a shape
// carrying just `id` keeps the deparser map clean of `as any`.
function readUomId(uom: unknown): string | null {
  const narrowed = (uom ?? {}) as { id?: string };
  return narrowed.id || null;
}

// Per-field deparser map for `deparseUpdateV2`. The `satisfies` clause is the
// exhaustiveness gate — adding a key to `ProjectFormSchemaInterface`
// without an entry here fails TypeScript compilation.
//
// Each entry returns a fragment of `Partial<Project_Update>` that gets merged
// into the result when `dirtyFields[key]` is truthy. Entries for schema fields
// without a BE Update equivalent (id, company, account, state, materialRates,
// fileAttachments) return `{}` — those values are read by other surfaces
// (state-events API, files API, URL params).
const projectFieldDeparsersUpdateV2 = {
  // --- Core identity & metadata ---
  id: () => ({}), // URL param, never in body.
  name: (f) => ({ name: f.name }),
  company: () => ({}), // Project_Update has no `company_id`.
  account: () => ({}), // Project_Update has no `account_id`.
  state: () => ({}), // State transitions go through the state-events API.
  externalId: (f) => ({ external_id: f.externalId || '' }),
  contract_number: (f) => ({ contract_number: f.contract_number || null }),
  dispatch_number: (f) => buildDispatchNumberForUpdate(f),
  poJobNumber: (f) => ({ po_job_number: f.poJobNumber || '' }),
  // --- Dates ---
  // Schema declares `effectiveAt` / `expiresAt` as yup `string().notRequired()`
  // which InferType widens to `Maybe<string | undefined>` (= `string | null |
  // undefined`). Project_Update declares both as `string | undefined` (not
  // nullable). Coerce null/empty back to `undefined` so the type lines up and
  // the BE doesn't receive an explicit null. `SingleDateTimeFormField` writes a
  // Dayjs object into form state (not an ISO string), which serializes to a
  // non-ISO `toString()` form (e.g. "Wed, 27 May 2026 17:00:00 GMT") on the
  // wire. Normalize with `dayjs(...).toISOString()` so the BE receives
  // "2026-05-27T17:00:00.000Z", matching the legacy deparse function.
  effectiveAt: (f) => ({
    effective_at: f.effectiveAt ? dayjs(f.effectiveAt).toISOString() : undefined,
  }),
  expiresAt: (f) => ({
    expires_at: f.expiresAt ? dayjs(f.expiresAt).toISOString() : undefined,
  }),
  // --- Notes ---
  // Schema has both `notes` and `projectNotes` (legacy duplicate). BE has one
  // `notes` field; legacy maps `projectNotes` to BE `notes`. We honor both —
  // either schema key being dirty emits the BE `notes` field. The form's
  // canonical source of truth is `projectNotes`.
  notes: (f) => ({ notes: f.notes || '' }),
  projectNotes: (f) => ({ notes: f.projectNotes || '' }),
  orderNotes: (f) => ({ order_notes: f.orderNotes || '' }),
  jobNotes: (f) => ({ job_notes: f.jobNotes || '' }),
  internalNotes: (f) => ({ internal_notes: f.internalNotes || '' }),
  // Quote PDF terms. Mirrors the legacy V1 deparse (project_terms, trimmed,
  // null when blank).
  projectTerms: (f) => ({ project_terms: f.projectTerms?.trim() || null }),
  // --- Sites & waypoints ---
  site: (f) => ({ site_id: f.site?.id || null }),
  pickUpWayPoint: (f) => ({ waypoints: buildWaypointsForUpdate(f) }),
  dropOffWayPoint: (f) => ({ waypoints: buildWaypointsForUpdate(f) }),
  stagingWayPoint: (f) => ({ waypoints: buildWaypointsForUpdate(f) }),
  // --- Repeating rows ---
  projectMaterialTotals: (f) => ({
    project_material_totals: buildProjectMaterialTotalsForUpdate(f),
  }),
  materialRates: () => ({}), // Material rates go through a separate API.
  allContacts: (f) => buildAllContactsForUpdate(f),
  phases: (f) => ({ phases: buildPhasesForUpdate(f) }),
  // --- People ---
  department: (f) => ({ department_id: f.department?.id || null }),
  foremen: (f) => ({ foreman_ids: (f.foremen ?? []).map((u) => u.id) }),
  collaborators: (f) => ({
    collaborator_ids: (f.collaborators ?? []).map((u) => u.id),
  }),
  salespeople: (f) => ({
    salesperson_ids: (f.salespeople ?? []).map((u) => u.id),
  }),
  requester: (f) => ({ requester_id: f.requester?.id || null }),
  // --- Service / class / equipment ---
  serviceClass: (f) => ({ service_class_id: f.serviceClass?.id || null }),
  chargeToAccount: (f) => ({ charge_to_account: f.chargeToAccount?.name ?? null }),
  jobTime: (f) => ({
    job_time:
      isNil(f.jobTime) || !String(f.jobTime).length ? null : Number(f.jobTime) * 60,
  }),
  orderQuantity: (f) => ({
    order_quantity: Number(f.orderQuantity) || null,
  }),
  orderUnitOfMeasure: (f) => {
    const id = readUomId(f.orderUnitOfMeasure);
    return {
      order_unit_of_measure: id ? (id as OrderUnitOfMeasure) : null,
    };
  },
  equipmentType: (f) => ({ equipment_type_id: f.equipmentType?.id || null }),
  equipmentTypeGrossCapacity: (f) => ({
    equipment_type_gross_capacity: f.equipmentTypeGrossCapacity
      ? Number(f.equipmentTypeGrossCapacity)
      : null,
  }),
  equipmentTypeGrossCapacityUnitOfMeasure: (f) => {
    const id = readUomId(f.equipmentTypeGrossCapacityUnitOfMeasure);
    return {
      equipment_type_gross_capacity_unit_of_measure: id
        ? (id as OrderUnitOfMeasure)
        : null,
    };
  },
  truckCount: (f) => ({
    // Clearing the NumberInput leaves '' in form state, and Number('') is 0 —
    // a truck_count of 0 is rejected by the BE. `|| null` collapses ''/0/NaN to
    // null so clearing the field resets it instead of submitting 0.
    truck_count: Number(f.truckCount) || null,
  }),
  unitsPerHour: (f) => ({ units_per_hour: f.unitsPerHour || null }),
  jobQuantity: (f) => ({ job_quantity: Number(f.jobQuantity) || null }),
  jobUnitOfMeasure: (f) => {
    const id = readUomId(f.jobUnitOfMeasure);
    return {
      job_unit_of_measure: id ? (id as OrderUnitOfMeasure) : null,
    };
  },
  loadUnitOfMeasure: (f) => ({
    load_unit_of_measure: f.loadUnitOfMeasure?.id
      ? (f.loadUnitOfMeasure.id as OrderUnitOfMeasure)
      : null,
  }),
  haulerRateType: (f) => ({ hauler_rate_type: f.haulerRateType?.value ?? null }),
  // --- Booleans ---
  recurringLoads: (f) => ({ recurring_loads: f.recurringLoads ?? undefined }),
  chaseMissingTickets: (f) => ({
    chase_missing_tickets: f.chaseMissingTickets ?? undefined,
  }),
  liveEta: (f) => ({ live_eta: f.liveEta ?? undefined }),
  defaultDigitalTicketView: (f) => ({
    default_digital_ticket_view: f.defaultDigitalTicketView ?? undefined,
  }),
  prevailingWage: (f) => ({ prevailing_wage: f.prevailingWage ?? undefined }),
  // TODO(TRE-15586): drop the cast after BE merges + /update-api-spec (field not yet in Project_Update)
  applyRateChangesToExistingOrders: (f) =>
    ({
      apply_rate_changes_to_existing_orders:
        f.applyRateChangesToExistingOrders ?? undefined,
    }) as Partial<Project_Update>,
  vendorPayCommissionPercentage: (f) => ({
    vendor_pay_commission_percentage: f.vendorPayCommissionPercentage,
  }),
  driverPayCommissionPercentage: (f) => ({
    driver_pay_commission_percentage: f.driverPayCommissionPercentage,
  }),
  materialRateItemized: (f) => ({
    material_rate_itemized: f.materialRateItemized ?? undefined,
  }),
  // --- Tax ---
  taxRateSchedule: (f) => ({
    tax_rate_schedule_id: f.taxRateSchedule?.value ?? null,
  }),
  taxRate: (f) => ({ tax_rate: isNil(f.taxRate) ? null : Number(f.taxRate) }),
  taxFreight: (f) => ({ tax_freight: f.taxFreight ?? false }),
  taxMaterial: (f) => ({ tax_material: f.taxMaterial ?? false }),
  taxAddOns: (f) => ({ tax_add_ons: f.taxAddOns ?? false }),
  // --- File attachments ---
  fileAttachments: () => ({}), // Files go through a separate upload API.
  // --- Side-channel rate fields ---
  // The 9 freight-rate keys (3 owners × 3 sub-fields) all collapse to the same
  // 3-tuple BE write per owner. Each schema key emits the full triplet so the
  // dirty-field gate is satisfied no matter which sub-field the user touched.
  customerRate: (f) =>
    deparseUpdateRateDetailsDTO('customer', {
      targetRate: f.customerRate,
      targetRateType: f.customerRateType,
      targetRateValue: f.customerRateValue,
    }),
  customerRateType: (f) =>
    deparseUpdateRateDetailsDTO('customer', {
      targetRate: f.customerRate,
      targetRateType: f.customerRateType,
      targetRateValue: f.customerRateValue,
    }),
  customerRateValue: (f) =>
    deparseUpdateRateDetailsDTO('customer', {
      targetRate: f.customerRate,
      targetRateType: f.customerRateType,
      targetRateValue: f.customerRateValue,
    }),
  vendorRate: (f) =>
    deparseUpdateRateDetailsDTO('vendor', {
      targetRate: f.vendorRate,
      targetRateType: f.vendorRateType,
      targetRateValue: f.vendorRateValue,
    }),
  vendorRateType: (f) =>
    deparseUpdateRateDetailsDTO('vendor', {
      targetRate: f.vendorRate,
      targetRateType: f.vendorRateType,
      targetRateValue: f.vendorRateValue,
    }),
  vendorRateValue: (f) =>
    deparseUpdateRateDetailsDTO('vendor', {
      targetRate: f.vendorRate,
      targetRateType: f.vendorRateType,
      targetRateValue: f.vendorRateValue,
    }),
  driverRate: (f) =>
    deparseUpdateRateDetailsDTO('driver', {
      targetRate: f.driverRate,
      targetRateType: f.driverRateType,
      targetRateValue: f.driverRateValue,
    }),
  driverRateType: (f) =>
    deparseUpdateRateDetailsDTO('driver', {
      targetRate: f.driverRate,
      targetRateType: f.driverRateType,
      targetRateValue: f.driverRateValue,
    }),
  driverRateValue: (f) =>
    deparseUpdateRateDetailsDTO('driver', {
      targetRate: f.driverRate,
      targetRateType: f.driverRateType,
      targetRateValue: f.driverRateValue,
    }),
} satisfies Record<
  keyof ProjectFormSchemaInterface,
  (formData: ProjectFormSchemaInterface) => Partial<Project_Update>
>;

export function deparseUpdateV2(
  formData: ProjectFormSchemaInterface,
  dirtyFields: ProjectFormDirtyFields,
): Partial<Project_Update> {
  const result: Partial<Project_Update> = {};
  const keys = Object.keys(projectFieldDeparsersUpdateV2) as Array<
    keyof typeof projectFieldDeparsersUpdateV2
  >;
  for (const key of keys) {
    if (!dirtyFields[key]) continue;
    Object.assign(result, projectFieldDeparsersUpdateV2[key](formData));
  }
  return result;
}

export function deparseCreateV2(formData: ProjectFormSchemaInterface): Project_Create {
  const contacts = buildAllContactsForCreate(formData);
  const customerRate = deparseRateDetailsDTO('customer', {
    targetRate: formData.customerRate,
    targetRateType: formData.customerRateType,
    targetRateValue: formData.customerRateValue,
  });
  const vendorRate = deparseRateDetailsDTO('vendor', {
    targetRate: formData.vendorRate,
    targetRateType: formData.vendorRateType,
    targetRateValue: formData.vendorRateValue,
  });
  const driverRate = deparseRateDetailsDTO('driver', {
    targetRate: formData.driverRate,
    targetRateType: formData.driverRateType,
    targetRateValue: formData.driverRateValue,
  });

  const orderUomId = readUomId(formData.orderUnitOfMeasure);
  const jobUomId = readUomId(formData.jobUnitOfMeasure);
  const equipmentUomId = readUomId(formData.equipmentTypeGrossCapacityUnitOfMeasure);
  const projectMaterialTotals = buildProjectMaterialTotalsForCreate(formData);
  const waypoints = buildWaypointsForCreate(formData);

  return {
    name: formData.name,
    ...(formData.company?.id ? { company_id: formData.company.id } : {}),
    ...(formData.effectiveAt
      ? { effective_at: dayjs(formData.effectiveAt).toISOString() }
      : {}),
    ...(formData.expiresAt
      ? { expires_at: dayjs(formData.expiresAt).toISOString() }
      : {}),
    notes: formData.projectNotes || '',
    order_notes: formData.orderNotes || '',
    job_notes: formData.jobNotes || '',
    internal_notes: formData.internalNotes || '',
    // Quote PDF terms — mirrors the legacy V1 create deparse.
    project_terms: formData.projectTerms?.trim() || undefined,
    po_job_number: formData.poJobNumber || '',
    external_id: formData.externalId || '',
    ...(formData.account?.id ? { account_id: formData.account.id } : {}),
    ...(formData.site?.id ? { site_id: formData.site.id } : {}),
    phases: buildPhasesForCreate(formData),
    ...contacts,
    ...(projectMaterialTotals ? { project_material_totals: projectMaterialTotals } : {}),
    department_id: formData.department?.id || null,
    foreman_ids: (formData.foremen ?? []).map((u) => u.id),
    collaborator_ids: (formData.collaborators ?? []).map((u) => u.id),
    salesperson_ids: (formData.salespeople ?? []).map((u) => u.id),
    service_class_id: formData.serviceClass?.id || null,
    job_time:
      isNil(formData.jobTime) || !String(formData.jobTime).length
        ? null
        : Number(formData.jobTime) * 60,
    order_quantity: formData.orderQuantity || null,
    equipment_type_id: formData.equipmentType?.id || null,
    // See the deparseUpdateV2 truckCount note: '' coerces to 0, which the BE
    // rejects, so `|| null` resets the field instead of submitting 0.
    truck_count: Number(formData.truckCount) || null,
    units_per_hour: formData.unitsPerHour || null,
    job_quantity: formData.jobQuantity || null,
    waypoints,
    ...customerRate,
    ...vendorRate,
    ...driverRate,
    requester_id: formData.requester?.id || null,
    equipment_type_gross_capacity: formData.equipmentTypeGrossCapacity
      ? Number(formData.equipmentTypeGrossCapacity)
      : null,
    order_unit_of_measure: orderUomId ? (orderUomId as OrderUnitOfMeasure) : null,
    job_unit_of_measure: jobUomId ? (jobUomId as OrderUnitOfMeasure) : null,
    equipment_type_gross_capacity_unit_of_measure: equipmentUomId
      ? (equipmentUomId as OrderUnitOfMeasure)
      : null,
    load_unit_of_measure: formData.loadUnitOfMeasure?.id
      ? (formData.loadUnitOfMeasure.id as OrderUnitOfMeasure)
      : null,
    ...(formData.dispatch_number && formData.dispatch_number.trim() !== ''
      ? { dispatch_number: { name: formData.dispatch_number } }
      : {}),
    contract_number: formData.contract_number || null,
    hauler_rate_type: formData.haulerRateType?.value ?? null,
    recurring_loads: formData.recurringLoads ?? false,
    charge_to_account: formData.chargeToAccount?.name ?? null,
    chase_missing_tickets: formData.chaseMissingTickets ?? false,
    live_eta: formData.liveEta ?? false,
    default_digital_ticket_view: formData.defaultDigitalTicketView ?? false,
    prevailing_wage: formData.prevailingWage ?? false,
    vendor_pay_commission_percentage: formData.vendorPayCommissionPercentage,
    driver_pay_commission_percentage: formData.driverPayCommissionPercentage,
    material_rate_itemized: formData.materialRateItemized ?? false,
    tax_rate: isNil(formData.taxRate) ? null : Number(formData.taxRate),
    tax_freight: formData.taxFreight ?? false,
    tax_material: formData.taxMaterial ?? false,
    tax_add_ons: formData.taxAddOns ?? false,
    tax_rate_schedule_id: formData.taxRateSchedule?.value ?? null,
    // TODO(TRE-15586): drop the cast after BE merges + /update-api-spec (field not yet in Project_Create)
    ...({
      apply_rate_changes_to_existing_orders:
        formData.applyRateChangesToExistingOrders ?? false,
    } as Partial<Project_Create>),
  };
}

export class ProjectEmailDraft extends OutboundEmailDraft {
  constructor(
    private _project_ids: string[],
    draft: OutboundEmailDraftFields,
  ) {
    super(draft);
  }

  public static parse(proto: ProjectEmailDraft_Read) {
    return new ProjectEmailDraft(
      proto.project_ids,
      OutboundEmailDraft.parseFields(proto),
    );
  }

  public get ids() {
    return this._project_ids;
  }
}
